import {ChangeDetectionStrategy, Component, inject, OnDestroy, signal} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {DatePicker} from "primeng/datepicker";
import {Textarea} from "primeng/textarea";
import {MultiSelect} from "primeng/multiselect";
import {InputText} from "primeng/inputtext";
import {NotificationService} from "../../services/notification.service";
import {MessageService} from "primeng/api";
import {Observable, of, tap} from "rxjs";
import {SubmitButtonComponent} from "../../components/submit-button/submit-button.component";
import {Select} from "primeng/select";
import {MarketingNotificationFilters} from "admin-api-dto";

type StatusOption = 'active' | 'mandatory-flow' | 'signup-flow';
type PaymentStatusOption = 'paid' | 'not-paid';

@Component({
  selector: 'app-marketing-sms-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, SubmitButtonComponent, MultiSelect, DatePicker, Textarea, InputText, Select],
  templateUrl: './marketing-sms-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarketingSmsTabComponent implements OnDestroy {
  private readonly notificationService = inject(NotificationService);
  private readonly messageService = inject(MessageService);
  private pollTimer: ReturnType<typeof setInterval> | undefined;

  protected readonly statusOptions: { label: string; value: StatusOption }[] = [
    {label: 'Active', value: 'active'},
    {label: 'Mandatory flow', value: 'mandatory-flow'},
    {label: 'Signup flow', value: 'signup-flow'},
  ];

  protected readonly paymentStatusOptions: { label: string; value: PaymentStatusOption }[] = [
    {label: 'Paid', value: 'paid'},
    {label: 'Not paid', value: 'not-paid'},
  ];

  protected readonly profileCompleteOptions: { label: string; value: boolean | undefined }[] = [
    {label: 'Any', value: undefined},
    {label: 'Complete', value: true},
    {label: 'Incomplete', value: false},
  ];

  protected readonly lastSeenOnAppOptions: { label: string; value: boolean | undefined }[] = [
    {label: 'Any', value: undefined},
    {label: 'Yes (has app version)', value: true},
    {label: 'No (no app version)', value: false},
  ];

  protected selectedStatuses = signal<StatusOption[]>([]);
  protected selectedPaymentStatuses = signal<PaymentStatusOption[]>([]);
  protected profileComplete = signal<boolean | undefined>(undefined);
  protected lastSeenOnApp = signal<boolean | undefined>(undefined);
  protected lastSeenFrom = signal<Date | undefined>(undefined);
  protected lastSeenTo = signal<Date | undefined>(undefined);
  protected createdFrom = signal<Date | undefined>(undefined);
  protected createdTo = signal<Date | undefined>(undefined);
  protected readonly categoryOptions: { label: string; value: string | undefined }[] = [
    {label: 'Core (always sent)', value: 'core'},
    {label: 'Promotional', value: 'promotional'},
    {label: 'Lottery', value: 'lottery'},
    {label: 'Lottery follow-up', value: 'lotteryfollowup'},
  ];

  protected textMessage = signal('');
  protected emailTemplateId = signal('');
  protected pushText = signal('');
  protected category = signal<string | undefined>('core');

  protected readonly previewedUserIds = signal<string[] | undefined>(undefined);
  protected readonly previewLoading = signal(false);

  protected readonly hasAnyChannel = () =>
    !!this.textMessage().trim() || !!this.emailTemplateId().trim() || !!this.pushText().trim();

  private buildFilters(): MarketingNotificationFilters {
    return {
      status: this.selectedStatuses().length > 0 ? this.selectedStatuses() : undefined,
      paymentStatus: this.selectedPaymentStatuses().length > 0 ? this.selectedPaymentStatuses() : undefined,
      profileComplete: this.profileComplete(),
      lastSeenFrom: this.lastSeenFrom()?.toISOString() ?? undefined,
      lastSeenTo: this.lastSeenTo()?.toISOString() ?? undefined,
      createdFrom: this.createdFrom()?.toISOString() ?? undefined,
      createdTo: this.createdTo()?.toISOString() ?? undefined,
      lastSeenOnApp: this.lastSeenOnApp(),
    };
  }

  protected readonly preview: () => Observable<unknown> = () => {
    this.previewedUserIds.set(undefined);
    this.previewLoading.set(true);
    this.stopPolling();

    return this.notificationService.startMarketingNotificationPreview(this.buildFilters()).pipe(
      tap({
        next: (result) => {
          this.startPolling(result.jobId);
        },
        error: () => {
          this.previewLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Failed',
            detail: 'Could not start marketing notification preview.'
          });
        }
      })
    );
  }

  private startPolling(jobId: string) {
    this.pollTimer = setInterval(() => {
      this.notificationService.getMarketingNotificationPreviewResult(jobId).subscribe({
        next: (result) => {
          if (result.status === 'done' && result.userIds) {
            this.stopPolling();
            this.previewLoading.set(false);
            this.previewedUserIds.set(result.userIds);
            this.messageService.add({
              severity: 'info',
              summary: 'Preview',
              detail: `${result.userIds.length} users match the filters.`
            });
          } else if (result.status === 'error') {
            this.stopPolling();
            this.previewLoading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Failed',
              detail: 'Marketing notification preview failed.'
            });
          }
        },
        error: () => {
          this.stopPolling();
          this.previewLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Failed',
            detail: 'Could not check preview status.'
          });
        }
      });
    }, 3000);
  }

  private stopPolling() {
    if (this.pollTimer !== undefined) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  protected readonly send: () => Observable<unknown> = () => {
    const userIds = this.previewedUserIds();
    if (!userIds || userIds.length === 0) {
      this.messageService.add({severity: 'warn', summary: 'No users', detail: 'Preview first to find matching users.'});
      return of(undefined);
    }

    if (!this.hasAnyChannel()) {
      this.messageService.add({severity: 'warn', summary: 'Missing channel', detail: 'Please fill in at least one of SMS, email, or push.'});
      return of(undefined);
    }

    return this.notificationService.sendMarketingNotification(userIds, {
      textMessage: this.textMessage().trim() || undefined,
      emailTemplateId: this.emailTemplateId().trim() || undefined,
      pushText: this.pushText().trim() || undefined,
      category: this.category(),
    }).pipe(
      tap({
        next: (result) => {
          this.previewedUserIds.set(undefined);
          this.messageService.add({
            severity: 'success',
            summary: 'Sent',
            detail: `Queued notifications for ${result.count} users.`
          });
        },
        error: () => this.messageService.add({
          severity: 'error',
          summary: 'Failed',
          detail: 'Could not send marketing notification.'
        })
      })
    );
  }
}
